/**
 * DMCPricingTable Component
 * 
 * Displays DMC package pricing with multiple linked options (Accommodation + Pricing).
 * Per-person rates with dynamic pax columns.
 * 
 * Columns: Option | Adult (xN) | Extra Bed (xN) | Child w/Bed (xN) | Child w/o Bed (xN) | Infant (xN) | Subtotal | Discount | GST (X%) | Total
 * 
 * Features:
 * - Currency conversion (USD/EUR → INR + markup)
 * - Dynamic column hiding (0 pax = hidden)
 * - Discount column (hidden if all 0)
 * - GST column (hidden when gst_included = true)
 * 
 * Usage:
 *   <DMCPricingTable
 *     options={dmcParsed.pricing_options}
 *     paxConfig={{ adults: 2, extraBed: 1, childWithBed: 1, childNoBed: 0, infants: 0 }}
 *     currencyConfig={{ currency: 'USD', conversionRate: 83.5, markupPct: 10 }}
 *     gstConfig={{ percentage: 5, included: false }}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { CreditCard } from 'lucide-react';
import { COMPONENT_VARIANTS, formatCurrency } from './displayConfig';
import PricingMobileCards from './PricingMobileCards';

const DMCPricingTable = ({
  options = [],
  paxConfig = {},
  currencyConfig = {},
  gstConfig = {},
  variant = 'detail',
  showTitle = true,
  className = ''
}) => {
  if (!options || options.length === 0) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;
  
  // Pax configuration
  const { 
    adults = 0, 
    adultsInRooms = adults,
    extraBed = 0, 
    childWithBed = 0, 
    childNoBed = 0, 
    infants = 0 
  } = paxConfig;
  
  // Currency configuration
  const { 
    currency = 'INR', 
    conversionRate = 1, 
    markupPct = 0 
  } = currencyConfig;
  const needsConversion = currency !== 'INR' && conversionRate > 1;
  const markupMultiplier = 1 + (markupPct / 100);
  
  // GST configuration
  const { percentage: gstPct = 5, included: gstIncluded = false } = gstConfig;
  
  // Determine which columns to show (hide 0 pax columns)
  const hasExtraBed = extraBed > 0 && options.some(opt => (opt.extra_adult || 0) > 0);
  const hasChildWithBed = childWithBed > 0 && options.some(opt => (opt.child_with_bed || 0) > 0);
  const hasChildNoBed = childNoBed > 0 && options.some(opt => (opt.child_no_bed || 0) > 0);
  const hasInfants = infants > 0 && options.some(opt => (opt.infant || 0) > 0);
  
  // Check if any option has discount
  const hasDiscount = options.some(opt => (opt.discount || 0) > 0);

  // Calculate row values
  const calculateRow = (opt) => {
    const rate = needsConversion ? conversionRate : 1;
    
    const perPerson = (opt.per_person || 0) * rate * markupMultiplier;
    const extraAdultRate = (opt.extra_adult || 0) * rate * markupMultiplier;
    const childWithBedRate = (opt.child_with_bed || 0) * rate * markupMultiplier;
    const childNoBedRate = (opt.child_no_bed || 0) * rate * markupMultiplier;
    const infantRate = (opt.infant || 0) * rate * markupMultiplier;
    
    const adultTotal = perPerson * adultsInRooms;
    const extraBedTotal = extraAdultRate * extraBed;
    const childWBTotal = childWithBedRate * childWithBed;
    const childNBTotal = childNoBedRate * childNoBed;
    const infantTotal = infantRate * infants;
    
    const subtotal = adultTotal + extraBedTotal + childWBTotal + childNBTotal + infantTotal;
    const discount = (opt.discount || 0) * rate * markupMultiplier;
    const afterDiscount = subtotal - discount;
    const gstAmount = afterDiscount * (gstPct / 100);
    const total = afterDiscount + gstAmount;
    
    return {
      perPerson,
      extraAdultRate,
      childWithBedRate,
      childNoBedRate,
      infantRate,
      subtotal,
      discount,
      gstAmount,
      total
    };
  };

  return (
    <div 
      className={className}
      data-package-type="dmc_package"
      data-component="dmc-pricing-table"
      data-testid="dmc-pricing-table"
    >
      {showTitle && (
        <h3 className={`font-semibold ${variant === 'preview' ? 'text-sm' : 'text-lg'} mb-3 flex items-center gap-2`}>
          <CreditCard className={`${variant === 'preview' ? 'w-4 h-4' : 'w-5 h-5'} text-[var(--navy)]`} />
          Package Options & Prices
        </h3>
      )}
      
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className={`${styles.headerBg} ${styles.headerText} px-4 py-2 font-semibold`}>
          Pricing by Category
        </div>
        
        <div className="p-3">
          {/* Currency conversion note */}
          {needsConversion && (
            <div className={`mb-3 ${variant === 'preview' ? 'text-[10px]' : 'text-xs'} text-[var(--navy)] bg-[color-mix(in_srgb,var(--navy)_5%,white)] px-2 py-1 rounded inline-block`}>
              Converted from {currency} @ 1 {currency} = ₹{conversionRate.toFixed(2)} INR
              {markupPct > 0 && ` + ${markupPct}% markup`}
            </div>
          )}

          {/* Mobile stacked cards (<640px) — avoids clipped columns at 360px */}
          <div className="sm:hidden">
            <PricingMobileCards
              type="dmc"
              options={options}
              paxConfig={paxConfig}
              gstConfig={gstConfig}
              currencyConfig={currencyConfig}
            />
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className={`w-full ${styles.fontSize} border ${styles.borderColor}`}>
              <thead>
                <tr className={`${styles.headerBg} ${styles.headerText}`}>
                  <th className={`border ${styles.borderColor} ${styles.padding} text-left font-semibold`}>
                    Category
                  </th>
                  <th className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold`}>
                    Adult ({adultsInRooms})
                  </th>
                  {hasExtraBed && (
                    <th className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold`}>
                      Extra Bed ({extraBed})
                    </th>
                  )}
                  {hasChildWithBed && (
                    <th className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold`}>
                      Child w/Bed ({childWithBed})
                    </th>
                  )}
                  {hasChildNoBed && (
                    <th className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold`}>
                      Child w/o Bed ({childNoBed})
                    </th>
                  )}
                  {hasInfants && (
                    <th className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold`}>
                      Infant ({infants})
                    </th>
                  )}
                  <th className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold bg-[color-mix(in_srgb,var(--navy)_20%,white)]`}>
                    Subtotal
                  </th>
                  {hasDiscount && (
                    <th className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold bg-[color-mix(in_srgb,var(--cyan)_25%,white)]`}>
                      Discount
                    </th>
                  )}
                  {!gstIncluded && (
                    <th className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold bg-[color-mix(in_srgb,var(--navy)_20%,white)]`}>
                      GST ({gstPct}%)
                    </th>
                  )}
                  <th className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold bg-[color-mix(in_srgb,var(--navy)_30%,white)]`}>
                    {gstIncluded ? `Total (incl. GST)` : 'Total'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {options.map((opt, idx) => {
                  const row = calculateRow(opt);
                  
                  return (
                    <tr 
                      key={idx}
                      className={idx % 2 === 0 ? (variant === 'preview' ? 'bg-slate-50' : 'bg-gray-50') : 'bg-white'}
                    >
                      <td className={`border ${styles.borderColor} ${styles.padding} font-medium`}>
                        {opt.option_name || opt.category || `Option ${idx + 1}`}
                      </td>
                      <td className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold text-[var(--navy)]`}>
                        {formatCurrency(row.perPerson)}
                      </td>
                      {hasExtraBed && (
                        <td className={`border ${styles.borderColor} ${styles.padding} text-center`}>
                          {row.extraAdultRate > 0 ? formatCurrency(row.extraAdultRate) : '-'}
                        </td>
                      )}
                      {hasChildWithBed && (
                        <td className={`border ${styles.borderColor} ${styles.padding} text-center`}>
                          {row.childWithBedRate > 0 ? formatCurrency(row.childWithBedRate) : '-'}
                        </td>
                      )}
                      {hasChildNoBed && (
                        <td className={`border ${styles.borderColor} ${styles.padding} text-center`}>
                          {row.childNoBedRate > 0 ? formatCurrency(row.childNoBedRate) : '-'}
                        </td>
                      )}
                      {hasInfants && (
                        <td className={`border ${styles.borderColor} ${styles.padding} text-center`}>
                          {row.infantRate > 0 ? formatCurrency(row.infantRate) : '-'}
                        </td>
                      )}
                      <td className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold bg-[color-mix(in_srgb,var(--navy)_5%,white)]`}>
                        {formatCurrency(row.subtotal)}
                      </td>
                      {hasDiscount && (
                        <td className={`border ${styles.borderColor} ${styles.padding} text-center font-semibold text-[var(--navy)] bg-[color-mix(in_srgb,var(--cyan)_10%,white)]`}>
                          {row.discount > 0 ? `- ${formatCurrency(row.discount)}` : '-'}
                        </td>
                      )}
                      {!gstIncluded && (
                        <td className={`border ${styles.borderColor} ${styles.padding} text-center text-gray-600 bg-[color-mix(in_srgb,var(--navy)_5%,white)]`}>
                          {formatCurrency(row.gstAmount)}
                        </td>
                      )}
                      <td className={`border ${styles.borderColor} ${styles.padding} text-center font-bold text-[var(--navy)] bg-[color-mix(in_srgb,var(--navy)_10%,white)]`}>
                        {formatCurrency(row.total)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* GST included note */}
          {gstIncluded && (
            <p className={`mt-2 ${variant === 'preview' ? 'text-[10px]' : 'text-xs'} text-gray-500`}>
              * GST @ {gstPct}% is included in the total
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DMCPricingTable;
